import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Spacer,
    Spinner,
    Text,
    useDisclosure,
    VStack,
} from "@chakra-ui/react";
import { IconAddressBook, IconDotsVertical, IconEdit, IconPlus, IconRefresh, IconSearch, IconTrash, IconX } from "@tabler/icons-react";
import { AddressBookEntry, useAddressBookEntries, useDeleteAddressBookEntry } from "../../api";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { SetPageTitle } from "../../components/SetPageTitle";
import { AddressBookEditorModal } from "./AddressBookEditorModal";

export const AddressBookPage: FunctionComponent = () => {
    const appInsights = useAppInsightsContext();
    const entriesQuery = useAddressBookEntries();
    const deleteEntry = useDeleteAddressBookEntry();
    const editor = useDisclosure();
    const deleteDialog = useDisclosure();
    const [search, setSearch] = useState("");
    const [entryToEdit, setEntryToEdit] = useState<AddressBookEntry>();
    const [entryToDelete, setEntryToDelete] = useState<AddressBookEntry>();
    const entryCount = entriesQuery.data?.length ?? 0;

    useEffect(() => {
        appInsights.trackPageView({ uri: "/admin/address-book" });
    }, [appInsights]);

    const filteredEntries = useMemo(() => {
        const entries = entriesQuery.data ?? [];
        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) {
            return entries;
        }
        return entries.filter(entry =>
            [entry.displayName, entry.name, entry.description, entry.phone, entry.address?.street1, entry.address?.city, entry.address?.state, entry.address?.zipCode].some(value =>
                value?.toLowerCase().includes(normalizedSearch)
            )
        );
    }, [entriesQuery.data, search]);

    const openCreate = () => {
        setEntryToEdit(undefined);
        editor.onOpen();
    };

    const openEdit = (entry: AddressBookEntry) => {
        setEntryToEdit(entry);
        editor.onOpen();
    };

    const openDelete = (entry: AddressBookEntry) => {
        setEntryToDelete(entry);
        deleteDialog.onOpen();
    };

    const confirmDelete = async () => {
        if (!entryToDelete) {
            return;
        }
        await deleteEntry.mutateAsync(entryToDelete.id);
        deleteDialog.onClose();
        setEntryToDelete(undefined);
    };

    return (
        <Box h="100%" overflow="auto" bg="gray.50">
            <SetPageTitle title="System Settings" />
            <Flex align="center" px={6} py={4} bg="white" borderBottom="1px solid" borderColor="gray.200">
                <HStack gap={2}>
                    <Text fontSize="xl" fontWeight="semibold">
                        Address Book
                    </Text>
                    <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={2}>
                        {entryCount}
                    </Badge>
                </HStack>
                <Spacer />
                <HStack gap={2}>
                    <Button leftIcon={<Icon as={IconPlus} />} size="sm" colorScheme="blue" onClick={openCreate}>
                        Create
                    </Button>
                    <IconButton
                        aria-label="Refresh address book"
                        icon={<Icon as={IconRefresh} />}
                        size="sm"
                        variant="outline"
                        onClick={() => entriesQuery.refetch()}
                        isLoading={entriesQuery.isFetching}
                    />
                </HStack>
            </Flex>

            <Box px={6} py={4}>
                <HStack mb={3} gap={3}>
                    <InputGroup maxW="360px" size="sm">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={IconSearch} color="gray.400" boxSize={4} />
                        </InputLeftElement>
                        <Input placeholder="Find an address book entry..." value={search} onChange={event => setSearch(event.target.value)} bg="white" borderRadius="md" />
                    </InputGroup>
                    {search && (
                        <Button size="sm" variant="ghost" leftIcon={<Icon as={IconX} boxSize={3} />} onClick={() => setSearch("")}>
                            Clear
                        </Button>
                    )}
                    <Spacer />
                    <Text fontSize="sm" color="gray.500">
                        {filteredEntries.length} entr{filteredEntries.length === 1 ? "y" : "ies"}
                    </Text>
                </HStack>

                {entriesQuery.isError && (
                    <Alert status="error" mb={3}>
                        <AlertIcon />
                        Address book entries could not be loaded.
                    </Alert>
                )}

                <Flex
                    px={4}
                    py={2}
                    bg="gray.100"
                    borderTopRadius="md"
                    border="1px solid"
                    borderBottomWidth={0}
                    borderColor="gray.200"
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wide"
                >
                    <Box flex="1">Entry</Box>
                    <Box w="320px" display={{ base: "none", md: "block" }}>
                        Mailing address
                    </Box>
                    <Box w="40px" />
                </Flex>
                <Box bg="white" border="1px solid" borderColor="gray.200" borderBottomRadius="md" overflow="hidden">
                    {entriesQuery.isLoading ? (
                        <Flex py={12} justify="center">
                            <Spinner />
                        </Flex>
                    ) : filteredEntries.length === 0 ? (
                        <VStack py={12} color="gray.400" gap={2}>
                            <Icon as={IconAddressBook} boxSize={8} />
                            <Text fontSize="sm">{search ? "No address book entries found" : "No address book entries yet"}</Text>
                        </VStack>
                    ) : (
                        filteredEntries.map((entry, index) => (
                            <AddressBookListRow
                                key={entry.id}
                                entry={entry}
                                isLast={index === filteredEntries.length - 1}
                                onEdit={() => openEdit(entry)}
                                onDelete={() => openDelete(entry)}
                            />
                        ))
                    )}
                </Box>
            </Box>

            <AddressBookEditorModal entry={entryToEdit} isOpen={editor.isOpen} onClose={editor.onClose} />
            <ConfirmationDialog
                isOpen={deleteDialog.isOpen}
                onClose={deleteDialog.onClose}
                title="Delete address book entry"
                confirmLabel="Delete"
                confirmColorScheme="red"
                isLoading={deleteEntry.isLoading}
                onConfirm={confirmDelete}
            >
                Delete <strong>{entryToDelete?.displayName || entryToDelete?.name}</strong>? This entry will no longer be available to copy to contacts.
            </ConfirmationDialog>
        </Box>
    );
};

interface AddressBookListRowProps {
    entry: AddressBookEntry;
    isLast: boolean;
    onEdit: () => void;
    onDelete: () => void;
}

const AddressBookListRow: FunctionComponent<AddressBookListRowProps> = ({ entry, isLast, onEdit, onDelete }) => {
    const address = entry.address;
    return (
        <Flex px={4} py={3} align="center" gap={3} borderBottom={isLast ? undefined : "1px solid"} borderColor="gray.100" _hover={{ bg: "gray.50" }}>
            <Box flex="1" minW={0}>
                <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                    {entry.displayName || entry.name}
                </Text>
                {entry.displayName && (
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {entry.name}
                    </Text>
                )}
                {entry.description && (
                    <Text fontSize="xs" color="gray.400" noOfLines={1}>
                        {entry.description}
                    </Text>
                )}
            </Box>
            <Box w="320px" display={{ base: "none", md: "block" }}>
                <Text fontSize="xs" color="gray.600" noOfLines={1}>
                    {[address?.street1, address?.street2].filter(Boolean).join(", ")}
                </Text>
                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {[address?.city, address?.state, address?.zipCode].filter(Boolean).join(", ")}
                </Text>
            </Box>
            <Menu>
                <MenuButton as={IconButton} icon={<Icon as={IconDotsVertical} boxSize={4} />} aria-label="Address book entry actions" size="xs" variant="ghost" w="40px" />
                <MenuList minW="130px">
                    <MenuItem icon={<Icon as={IconEdit} />} onClick={onEdit}>
                        Edit
                    </MenuItem>
                    <MenuItem icon={<Icon as={IconTrash} />} onClick={onDelete}>
                        Delete
                    </MenuItem>
                </MenuList>
            </Menu>
        </Flex>
    );
};
